import { useEffect, useRef } from 'react';

type BonusVideoOverlayProps = {
    videoSrc: string | null;
    currentTheme: { color: { point: string } };
    onClose: () => void;
};

const BonusVideoOverlay: React.FC<BonusVideoOverlayProps> = ({
    videoSrc,
    currentTheme,
    onClose,
}) => {
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeRef.current();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const onEnded = () => closeRef.current();
        video.addEventListener('ended', onEnded);
        video.play().catch(() => {
            video.muted = true;
            video.play();
        });
        return () => video.removeEventListener('ended', onEnded);
    }, [videoSrc]);

    return (
        <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
        >
            <div className="flex flex-col items-center gap-5">
                <p className="text-3xl font-black text-white drop-shadow-lg">
                    Awesome! 🎉
                </p>
                <p className="text-lg text-white/80">
                    You finished early — enjoy!
                </p>
                {videoSrc ? (
                    <video
                        ref={videoRef}
                        src={videoSrc}
                        className="w-full max-w-lg rounded-2xl shadow-2xl"
                        playsInline
                        controls={false}
                        autoPlay
                    />
                ) : (
                    <div className="text-7xl">🌟</div>
                )}
                <button
                    onClick={onClose}
                    className="rounded-full px-10 py-3 text-lg font-bold text-white transition-all hover:scale-105"
                    style={{ backgroundColor: `${currentTheme.color.point}cc` }}
                >
                    ✅ Continue
                </button>
            </div>
        </div>
    );
};

export default BonusVideoOverlay;
